import type { Vector3 } from 'three'
import type { components } from '@/types/api'
import type { NodeAxisFeature } from '@/types/api'
import type { DAGNode, PadicFeatures, AxisFeature } from '@/types/visualization'

// Use auto-generated types from OpenAPI spec
type Message = components['schemas']['MessageSummary']

/**
 * Transform API message to DAG node for visualization
 * Now using auto-generated OpenAPI types
 */
export function transformApiMessageToDAGNode(
  apiMessage: Message,
  options: {
    position?: Vector3
    children?: string[]
    includeDefaults?: boolean
  } = {}
): DAGNode {
  const {
    position = { x: 0, y: 0, z: 0 } as Vector3,
    children = [],
    includeDefaults = true
  } = options

  // Transform p-adic features
  const padicFeatures = transformNodeFeaturesToPadic(apiMessage.features)

  // Calculate security and energy defaults if not provided
  const security = apiMessage.security_score ?? (includeDefaults ? calculateSecurityScore(apiMessage) : 0)
  const energy = apiMessage.energy ?? (includeDefaults ? calculateEnergyScore(apiMessage) : 0)

  return {
    id: apiMessage.id ?? '',  // Handle optional id
    position,
    depth: apiMessage.depth ?? 0,  // Handle optional depth
    timestamp: new Date(apiMessage.timestamp ?? Date.now()),  // Handle optional timestamp
    proposer: apiMessage.proposer_pk ?? '',  // Map proposer_pk to proposer, handle null
    parents: apiMessage.parents ?? [],  // Handle optional parents
    children,
    features: padicFeatures,
    finality: {
      kCore: apiMessage.k_core_finalized,
      homology: apiMessage.homology_finalized,
      kCoreValue: apiMessage.k_core_value ?? undefined,  // Use backend value if available
      homologyBetti: apiMessage.homology_betti_b1 ?? undefined  // Use backend value if available
    },
    security,
    energy
  }
}

/**
 * Transform node API features to structured p-adic features
 */
export function transformNodeFeaturesToPadic(features: any): PadicFeatures {
  if (!features || !features.axes || !Array.isArray(features.axes)) {
    return { axes: [] }
  }

  const axes: AxisFeature[] = features.axes.map((nodeAxis: NodeAxisFeature) => ({
    axis: nodeAxis.axis,
    value: calculateAxisValue(nodeAxis.digits, nodeAxis.p),
    digits: nodeAxis.digits,
    p: nodeAxis.p
  }))

  return { axes }
}

/**
 * Calculate axis value from p-adic digits
 */
function calculateAxisValue(digits: number[], p: number): number {
  return digits.reduce((sum, digit, index) => {
    return sum + digit * Math.pow(p, index)
  }, 0)
}

/**
 * Calculate security score from message properties
 */
function calculateSecurityScore(message: Message): number {
  let score = 0

  // Base score from depth (handle optional)
  score += (message.depth ?? 0) * 10

  // Bonus for having parents (handle optional)
  score += (message.parents ?? []).length * 5

  // Bonus for finality
  if (message.k_core_finalized) score += 50
  if (message.homology_finalized) score += 30

  // Normalize to 0-100 range
  return Math.min(100, score)
}

/**
 * Calculate energy score from message properties
 */
function calculateEnergyScore(message: Message): number {
  // Simple energy calculation based on connectivity (handle optional fields)
  const parentEnergy = (message.parents ?? []).length * 10
  const depthEnergy = (message.depth ?? 0) * 5
  const finalityEnergy = (message.k_core_finalized ? 25 : 0) + (message.homology_finalized ? 15 : 0)

  return parentEnergy + depthEnergy + finalityEnergy
}

/**
 * Calculate position for DAG layout based on depth and timestamp
 */
export function calculateDAGPosition(
  message: Message,
  layoutConfig: {
    depthSpacing?: number
    timeSpacing?: number
    radialSpread?: number
  } = {}
): Vector3 {
  const {
    depthSpacing = 100,
    timeSpacing = 50,
    radialSpread = 200
  } = layoutConfig

  // Use timestamp for time-based positioning (handle optional)
  const timestamp = new Date(message.timestamp ?? Date.now()).getTime()
  const timeIndex = Math.floor(timestamp / 60000) // Group by minute

  // Calculate radial position based on message ID for spread (handle optional)
  const hash = hashString(message.id ?? '')
  const angle = (hash % 360) * (Math.PI / 180)
  const radius = radialSpread + (hash % 100)

  return {
    x: Math.cos(angle) * radius,
    y: (message.depth ?? 0) * depthSpacing,
    z: Math.sin(angle) * radius + (timeIndex % 10) * timeSpacing
  } as Vector3
}

/**
 * Simple hash function for consistent positioning
 */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

/**
 * Transform multiple API messages to DAG nodes with calculated children
 * Now using auto-generated OpenAPI types
 */
export function transformMessagesToDAGNodes(messages: Message[]): DAGNode[] {
  // First pass: create basic nodes
  const nodes = new Map<string, DAGNode>()

  messages.forEach(message => {
    const position = calculateDAGPosition(message)
    const node = transformApiMessageToDAGNode(message, { position })
    const id = message.id ?? ''
    if (id) {
      nodes.set(id, node)
    }
  })

  // Second pass: calculate children relationships (handle optional fields)
  messages.forEach(message => {
    const messageId = message.id ?? ''
    const parents = message.parents ?? []

    parents.forEach(parentId => {
      const parentNode = nodes.get(parentId)
      if (parentNode && messageId && !parentNode.children.includes(messageId)) {
        parentNode.children.push(messageId)
      }
    })
  })

  return Array.from(nodes.values())
}